// shared-lib/estimate-pricing/designCenter/surfaceRegions.js
//
// The Design Center's rules for which region of a wall or floor is the
// surface's OWN finish and which sit on it, and which objects are slabs it
// re-finishes as a countertop. Moved out of web-app/src/hooks/useDesignCenter.js
// so the server's what-if pricing (`../optionChanges.js`) changes a design
// exactly as the panel's own pick does — one copy, not two that drift.

const { isShowerFloorRegion } = require('../utils/showerFloorRegion');

/**
 * A wall/floor region belonging to the SHOWER rather than the surface's own
 * finish. Was written out three times inline (selection detection, the shower
 * finder, and `updateSurfaceTextures`' preserve filter); they must agree, so
 * they share this.
 */
const isShowerSurfaceRegion = (r) =>
  Boolean(
    r?.regionId?.includes('shower') ||
      r?.source === 'shower' ||
      r?.surfaceType === 'Shower Wall'
  );

/**
 * A region that sits ON a surface rather than being the surface's own finish:
 * a backsplash band, a shower tile, a hand-drawn wainscot.
 *
 * This used to be `isShowerSurfaceRegion` alone — showers were the only
 * overlay when it was written — and every consumer of it inherited that
 * blind spot. Picking a wall colour rebuilt `texture.regions` as
 * `[newBase, ...showerRegions]`, so it DELETED the room's backsplash
 * (reported from the app), and it deleted a hand-drawn region too. The panel's
 * own detection had the mirror bug: `baseRegionOf` took the first non-shower
 * region, which after a backsplash could be a backsplash band, so the Wall
 * carousel could show a backsplash tile as the wall's finish.
 *
 * THREE signals, any of which makes a region an overlay. Measured over the
 * corpus (2,290 wall regions): every one of the 2,251 base regions is
 * full-coverage with no `surfaceType`, and every partial region is an overlay
 * — 15 shower, 3 `showerWall`, 24 hand-drawn. So the geometric test alone
 * would do; the other two are kept because they are cheap and a base region
 * is defined by `buildSavedRegion` writing 0,0,100,100, not by a rule anyone
 * enforces on data arriving from elsewhere.
 *
 * "Overlay" is about where a region SITS, not whether a new base keeps it:
 * the redesign's paint band above half-height tile is partial, so it is an
 * overlay here, but a whole-room finish pick replaces it — see
 * `replaceBaseFinish` below, which every pick must write through.
 */
const isFullCoverageRegion = (r) =>
  r?.x === 0 && r?.y === 0 && r?.width === 100 && r?.height === 100;

const isOverlayRegion = (r) =>
  isShowerSurfaceRegion(r) ||
  Boolean(r?.surfaceType) ||
  !isFullCoverageRegion(r);

/** The region carrying a surface's own finish — never an overlay. */
const baseRegionOf = (surface) =>
  (surface?.texture?.regions || []).find((r) => !isOverlayRegion(r));

/**
 * The marker on a WAINSCOT: a band of wall tile from the floor, its own kind
 * of region like a backsplash or a shower surround. The bathroom redesign
 * stamps it on the half-height tile it writes
 * (`wallTreatments.buildRegionsFromTreatment`); designs written before that
 * get it from `functions/scripts/backfill-wainscot-marker.js`.
 *
 * A MARKER, never a shape (decided on #2712): a full-width band from the floor
 * is also what a hand-drawn tile patch can look like, and only the marker
 * says the tile has a role.
 */
const WAINSCOT_SURFACE_TYPE = 'wainscot';

const isWainscotRegion = (r) => r?.surfaceType === WAINSCOT_SURFACE_TYPE;

/**
 * A region's `surfaceType` as the SURFACE it is: a wainscot is wall tile, so
 * it reads as no `surfaceType` at all (null), like any plain wall region.
 *
 * Only the whole-room pick (`replaceBaseFinish`) treats a wainscot as its own
 * kind. Everything else — the texture picker's application type, the
 * editor's grouping and matching, labels — must see exactly what it saw
 * before the marker existed, so it reads `surfaceType` through this. (The
 * take-off, the elevation drawing and the schedules never branch on an
 * unknown `surfaceType`, so they need nothing.)
 */
const asSurfaceKind = (surfaceType) =>
  surfaceType === WAINSCOT_SURFACE_TYPE ? null : (surfaceType ?? null);

/** Two regions are the same kind of surface (see `asSurfaceKind`). */
const sameSurfaceKind = (a, b) =>
  asSurfaceKind(a?.surfaceType) === asSurfaceKind(b?.surfaceType);

/**
 * A region with a picker or a role of its own, MARKED as such: a shower
 * surround or pan, a tub surround, a backsplash, a wainscot — any region
 * carrying a `surfaceType`, or the shower's / tub's own `source`.
 *
 * Everything else is the room's plain finish: paint (a whole wall, a band
 * above half-height tile, an accent), and tile with nothing saying what it is
 * for (a patch drawn by hand).
 */
const hasOwnRole = (r) =>
  Boolean(r?.surfaceType) ||
  isShowerSurfaceRegion(r) ||
  isShowerFloorRegion(r) ||
  r?.source === 'bathtub';

/**
 * Whether a whole-room pick laying `newBase` keeps `r` on top of it. Only a
 * region with its own role survives, and a wainscot only under PAINT: a Wall
 * Paint pick paints the wall above it, a Wall Tile pick tiles the whole wall
 * and owns the band too. (A Floor pick is a texture, and floors carry no
 * wainscot.)
 */
const keptByPick = (newBase) => (r) =>
  hasOwnRole(r) && !(isWainscotRegion(r) && newBase?.type !== 'paint');

/**
 * A surface's regions with its own finish replaced by `newBase` — what a
 * whole-room Wall Paint / Wall Tile / Floor pick writes, in the panel
 * (`useDesignCenter.updateSurfaceTextures`) and in the server's what-if
 * pricing (`optionChanges.withBaseRegion`), so the picture and the price
 * agree. The new base goes first, so everything kept draws over it.
 *
 *   region                     Wall Paint pick   Wall Tile pick
 *   paint (bands, accents)     replaced          replaced
 *   shower / tub tile          kept              kept
 *   backsplash                 kept              kept
 *   wainscot                   KEPT              replaced
 *   hand-drawn tile patch      removed           removed
 *
 * The bug this fixed (#2712): the bathroom redesign writes a half-tiled wall
 * as two partial regions and no full-coverage base, and a pick that kept every
 * partial region laid its new colour UNDER the old paint band — the upper
 * wall kept the old colour.
 */
const replaceBaseFinish = (regions, newBase) => [
  newBase,
  ...(regions || []).filter(keptByPick(newBase)),
];

/**
 * True when `replaceBaseFinish(regions, newBase)` would change nothing but the
 * base's `regionId`: the base already carries `newBase.id` and the pick would
 * remove nothing else. The panel asks it before offering Undo.
 */
const carriesBaseFinish = (surface, newBase) => {
  const base = baseRegionOf(surface);
  if (!base || base.id !== newBase?.id) return false;
  const kept = keptByPick(newBase);
  return (surface.texture.regions || []).every((r) => r === base || kept(r));
};

// Category-aware (#1535/M9): the oblique-corner kite is
// `corner-fill-countertop` with `category:{countertop:true}`, and legacy
// vanity slabs are `vanity-countertop` — the shared styling brain covers
// both, so the web panel must too or one slab keeps the old finish.
const isCountertopObject = (obj) =>
  obj.type === 'countertop' ||
  obj.type === 'tub-surface' ||
  obj.type === 'vanity-countertop' ||
  obj.category?.countertop === true;

module.exports = {
  isShowerSurfaceRegion,
  isFullCoverageRegion,
  isOverlayRegion,
  baseRegionOf,
  WAINSCOT_SURFACE_TYPE,
  isWainscotRegion,
  asSurfaceKind,
  sameSurfaceKind,
  hasOwnRole,
  replaceBaseFinish,
  carriesBaseFinish,
  isCountertopObject,
};
